import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type PointerEvent,
} from "react";

import { cn } from "../lib/cn";
import { useUiStrings } from "../lib/strings";
import { Icon } from "./Icon";

export interface ScrollXProps extends ComponentProps<"div"> {
  /** Accessible name of the scroll region (only announced while the content is wider than the box). */
  label?: string;
  scrollLeftLabel?: string;
  scrollRightLabel?: string;
  /** Class for the inner scrolling element. */
  viewportClassName?: string;
}

/** Below this many pixels of movement a press is a click, not a drag. */
const DRAG_THRESHOLD = 4;

/**
 * Side-scrolling box for content wider than the screen (tables, matrices). The browser's horizontal scrollbar is never
 * shown; instead the edges fade and arrow buttons appear when there is more to see. It also scrolls by touch,
 * shift + mouse wheel, trackpad, mouse drag and the arrow keys (the box is focusable while it overflows).
 */
export function ScrollX({
  children,
  className,
  viewportClassName,
  label,
  scrollLeftLabel,
  scrollRightLabel,
  ...props
}: ScrollXProps) {
  const strings = useUiStrings();
  const viewport = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: false, end: false });
  const drag = useRef<{ x: number; left: number; moved: boolean } | null>(null);

  const measure = useCallback(() => {
    const el = viewport.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const start = el.scrollLeft > 1;
    const end = el.scrollLeft < max - 1;
    setEdges((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, []);

  useEffect(() => {
    const el = viewport.current;
    if (!el) return;
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(el);
    if (el.firstElementChild) observer?.observe(el.firstElementChild);
    el.addEventListener("scroll", measure, { passive: true });
    return () => {
      observer?.disconnect();
      el.removeEventListener("scroll", measure);
    };
  }, [measure]);

  const overflowing = edges.start || edges.end;

  const page = (direction: 1 | -1) => {
    const el = viewport.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.max(el.clientWidth * 0.8, 120), behavior: "smooth" });
  };

  // Mouse drag to scroll (touch and pens already scroll natively).
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "mouse" || event.button !== 0 || !overflowing) return;
    const target = event.target as HTMLElement;
    if (
      target.closest("button, a, input, select, textarea, label, [role=button], [contenteditable]")
    )
      return;
    drag.current = { x: event.clientX, left: event.currentTarget.scrollLeft, moved: false };
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    if (!state) return;
    const dx = event.clientX - state.x;
    if (!state.moved && Math.abs(dx) < DRAG_THRESHOLD) return;
    if (!state.moved) {
      state.moved = true;
      event.currentTarget.setPointerCapture?.(event.pointerId);
    }
    event.currentTarget.scrollLeft = state.left - dx;
  };
  const endDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (drag.current?.moved) event.currentTarget.releasePointerCapture?.(event.pointerId);
    drag.current = null;
  };

  return (
    <div className={cn("relative w-full", className)} {...props}>
      <div
        ref={viewport}
        role={overflowing ? "region" : undefined}
        aria-label={overflowing ? label : undefined}
        tabIndex={overflowing ? 0 : undefined}
        data-overflowing={overflowing || undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className={cn(
          "scrollbar-none w-full overflow-x-auto overscroll-x-contain focus-visible:outline-offset-[-2px]",
          viewportClassName,
        )}
      >
        {children}
      </div>
      {edges.start && (
        <div className="pointer-events-none absolute inset-y-0 left-0 flex w-12 items-center bg-gradient-to-r from-surface to-transparent">
          <button
            type="button"
            tabIndex={-1}
            aria-label={scrollLeftLabel ?? strings.scrollLeft}
            onClick={() => page(-1)}
            className="pointer-events-auto ml-1 flex size-8 items-center justify-center rounded-full border border-border bg-surface text-foreground shadow-card hover:bg-surface-muted"
          >
            <Icon name="chevron-left" size={16} />
          </button>
        </div>
      )}
      {edges.end && (
        <div className="pointer-events-none absolute inset-y-0 right-0 flex w-12 items-center justify-end bg-gradient-to-l from-surface to-transparent">
          <button
            type="button"
            tabIndex={-1}
            aria-label={scrollRightLabel ?? strings.scrollRight}
            onClick={() => page(1)}
            className="pointer-events-auto mr-1 flex size-8 items-center justify-center rounded-full border border-border bg-surface text-foreground shadow-card hover:bg-surface-muted"
          >
            <Icon name="chevron-right" size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
